window.ToggleButton ??= class ToggleButton extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  connectedCallback() {
    this.style.cursor = 'pointer';
    this.style.userSelect = 'none';

    // A custom element is neither focusable nor announced as a control, so keyboard and screen-reader
    // users could not reveal the extra rows. Give it native-button semantics.
    this.setAttribute('role', 'button');
    if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '0');
    this.setAttribute('aria-expanded', this.querySelector('img.rotate') ? 'true' : 'false');
  }

  /**
   * Enter / Space activate the toggle, as they would a native button.
   * @param {KeyboardEvent} event
   */
  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.handleClick();
  }

  handleClick() {
    const { seeMore, seeLess } = window.utilsString.general;
    const parentTable = this.closest('.comparison-table-wrapper');
    const parentCard = this.closest('.comparison-cards');
    const productsCard = this.closest('.products-lists');
    const pricingTable = this.closest('.pricing-table-wrapper');
    const pricingCard = this.closest('.product-list');

    const buttonTextElem = this.querySelector('p');
    const isExpanded = this.querySelector('img').classList.toggle('rotate');
    this.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');

    if (parentTable) {
      this.toggleTableRows(parentTable, buttonTextElem, seeMore, seeLess, true);
    } else if (parentCard) {
      this.toggleCardRows(parentCard, buttonTextElem, seeMore, seeLess);
    } else if (productsCard) {
      this.toggleProductsFeaturesRows(productsCard, buttonTextElem, seeMore, seeLess);
    } else if (pricingTable) {
      this.toggleProductsPricingFeaturesRows(pricingTable, buttonTextElem, seeMore, seeLess);
    } else if (pricingCard) {
      this.togglePricingCardFeaturesRows(pricingCard, buttonTextElem, seeMore, seeLess);
    }
  }

  // Toggles the visibility of table rows with a smooth height reveal and updates the button state.
  toggleTableRows(table, buttonText, seeMore, seeLess) {
    const rows = table.querySelectorAll('table tbody tr.toggle-rows');
    const showRows = table.querySelector('table tbody').dataset.showRows;
    const updateBottomBorder = table.querySelector(`table tbody tr:nth-child(${showRows}) td:nth-child(2)`);
    const expanding = rows.length > 0 && rows[0].classList.contains('hidden');

    this.animateHeight(table, () => rows.forEach((row) => row.classList.toggle('hidden')));
    this.updateButtonState(buttonText, [updateBottomBorder], seeMore, seeLess);

    if (expanding) {
      this.fadeInRows(rows);
    } else {
      this.scrollIntoViewIfNeeded(table);
    }
  }

  // Toggles the visibility of card rows with a smooth height reveal and updates the button state.
  toggleCardRows(card, buttonText, seeMore, seeLess) {
    const rows = card.querySelectorAll('.compare-cards .toggle-rows');
    const expanding = rows.length > 0 && rows[0].classList.contains('hidden');

    this.animateHeight(card, () => rows.forEach((row) => row.classList.toggle('hidden')));
    this.updateButtonState(buttonText, null, seeMore, seeLess);

    if (expanding) {
      this.fadeInRows(rows);
    } else {
      this.scrollIntoViewIfNeeded(card);
    }
  }

  // Toggles the visibility of product pricing feature rows and updates the button state.
  toggleProductsPricingFeaturesRows(table, buttonText, seeMore, seeLess) {
    const rows = table.querySelectorAll('table tbody tr.toggle-rows');
    const showRows = table.querySelector('table tbody').dataset.showRows;

    const columns = [2, 3, 4].map((index) =>
      table.querySelector(`table tbody tr:nth-child(${showRows}) td:nth-child(${index})`)
    );

    rows.forEach((row) => row.classList.toggle('hidden'));
    this.updateButtonState(buttonText, columns, seeMore, seeLess);
  }

  // Toggles the visibility of pricing card rows and updates the button state.
  togglePricingCardFeaturesRows(card, buttonText, seeMore, seeLess) {
    const rows = card.querySelectorAll('.product-card .toggle-rows');
    rows.forEach((row) => row.classList.toggle('hidden'));
    this.updateButtonState(buttonText, null, seeMore, seeLess);
  }

  // Toggles the visibility of product feature rows and updates the button state.
  toggleProductsFeaturesRows(card, buttonText, seeMore, seeLess) {
    const rows = card.querySelectorAll('.products-list .toggle-rows');
    rows.forEach((row) => row.classList.toggle('hidden'));
    this.updateButtonState(buttonText, null, seeMore, seeLess);
  }

  // Updates the button text based on the current state (See More / See Less).
  updateButtonState(buttonText, borderElements, seeMore, seeLess) {
    if (buttonText.innerText === seeMore) {
      buttonText.innerText = seeLess;

      if (Array.isArray(borderElements)) {
        this.removeBorder(borderElements);
      }
    } else {
      buttonText.innerText = seeMore;

      if (Array.isArray(borderElements)) {
        this.removeStyleAttribute(borderElements);
      }
    }
  }

  // Removes specific border styles from the provided elements.
  removeBorder(borderElements) {
    borderElements.forEach((element) => {
      element.style.borderRadius = '0';
      element.style.borderBottom = '0';

      const hrLine = element.querySelector('.horizontal-line');
      if (hrLine) {
        hrLine.style.display = 'block';
      }
    });
  }

  // Removes the inline style attributes from the provided elements.
  removeStyleAttribute(borderElements) {
    borderElements.forEach((element) => {
      element.removeAttribute('style');

      const hrLine = element.querySelector('.horizontal-line');
      if (hrLine) {
        hrLine.removeAttribute('style');
      }
    });
  }

  // Animates a container's height between its pre- and post-mutation states (FLIP), running the mutation in between.
  animateHeight(container, mutate) {
    if (this.prefersReducedMotion()) {
      mutate();
      return;
    }

    const startHeight = container.offsetHeight;
    mutate();

    const endHeight = container.offsetHeight;
    if (startHeight === endHeight) return;

    // Clip overflow so revealed rows stay masked while the container grows.
    container.style.overflow = 'hidden';
    const animation = container.animate([{ height: `${startHeight}px` }, { height: `${endHeight}px` }], {
      duration: 350,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    });

    const cleanup = () => container.style.removeProperty('overflow');
    animation.finished.then(cleanup).catch(cleanup);
  }

  // Fades the newly revealed rows in with a small staggered offset.
  fadeInRows(rows) {
    if (this.prefersReducedMotion()) return;

    rows.forEach((row, index) => {
      row.animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 300,
        delay: Math.min(index * 25, 200),
        easing: 'ease-out',
        fill: 'backwards',
      });
    });
  }

  // Smoothly brings the element back into view after collapsing, only when it has scrolled above the viewport.
  // The offset clears the sticky header (its height differs between mobile and desktop) plus a small gap.
  scrollIntoViewIfNeeded(
    element,
    offset = (document.getElementById('shopify-section-header')?.offsetHeight || 80) + 12
  ) {
    const rect = element.getBoundingClientRect();

    if (rect.top >= 0) return;

    const top = rect.top + window.scrollY - offset;
    window.scrollTo({ top, behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
};

// Define the custom element
if (!customElements.get('toggle-rows-button')) {
  customElements.define('toggle-rows-button', window.ToggleButton);
}
